import { useEffect, useState, useRef } from "react";
import { useToast } from "../context/ToastContext";
import { diagnosticService,patientService, doctorService } from "../services";
import TestOrderView from "../components/diagnostic/TestOrderView";
import TestInvoiceView from "../components/health/TestInvoiceView";
import DataTable from "../components/common/DataTable";
import { Edit, Printer, Trash2, Plus, Minus, ChevronDown, ChevronUp, Loader } from "lucide-react";
import { formatUTCToLocal } from "../utils/dateUtils";
import SearchableDropdown from "../components/common/SearchableDropdown";
import DatePicker from "../components/common/DatePicker";
import { adminService, careService, healthService } from "../services/api";
import NumericInput from "../components/common/NumericInput";
import { toSafeFixed } from "../utils/numberUtils";


interface OrderPurchasePayment {
  test_order_number:String,
  order_date:String,
  patient_id:Number,
  patient_name: String,
  patient_phone: String,
  doctor_id:  Number,
  doctor_name: String,
  doctor_phone: String,
  doctor_license_number: String,
  //appointment_id: Number,
  agency_id: Number,
  subtotal_amount: Number,
  items_total_discount_amount: Number,
  taxable_amount: 2250,
  cgst_amount: 112.5,
  sgst_amount: 112.5,
  igst_amount: 0,
  cess_amount: 0,
  overall_disc_percentage: 10,
  overall_disc_amount: 225,
  overall_cess_percentage: 0,
  overall_cess_amount: 0,
  roundoff: 0,
  final_amount: 2250,
  urgency:String ,
  notes: String,
  tags: Array<String>,
  items:Array<items>,
  invoice_number:String,
  invoice_date:String,
  due_date: String,
  branch_id: Number,
  invoice_notes: String,
  invoice_tags: Array<String>,
  payment_number: String,
  payment_amount: Number,
  payment_mode: String,
  instrument_number: String,
  instrument_date: String,
  bank_name: String,
  branch_name: String,
  ifsc_code: String,
  transaction_reference: String,
  payment_remarks: String

}

interface items {
      line_no: Number,
      test_id: Number,
      test_name:String,
      panel_id: Number,
      panel_name: String,
      rate: Number,
      disc_percentage: Number,
      disc_amount: Number,
      taxable_amount: Number,
      cgst_rate: Number,
      cgst_amount:Number,
      sgst_rate: Number,
      sgst_amount: Number,
      igst_rate: Number,
      igst_amount: Number,
      cess_rate: Number,
      cess_amount: Number,
      total_amount: Number,
      remarks: String
}

interface OrderItem {
  test_id: number;
  test_name: string;
  panel_id: number;
  panel_name: string;
  is_panel: boolean;
  rate: number;
  cgst_rate: number;
  sgst_rate: number;
  igst_rate: number;
  cess_rate: number;
  disc_percentage: number;
  disc_amount: number;
  taxable_amount: number;
  cgst_amount: number;
  sgst_amount: number;
  igst_amount: number;
  cess_amount: number;
  total_amount: number;
}

const OrderPurchasePaymentPage: React.FC = ()=>{ 

 const [orders, setOrders] = useState<OrderPurchasePayment[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderPurchasePayment | null>(null);
  const [showOrderView, setShowOrderView] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any | null>(null);
  const [showInvoiceView, setShowInvoiceView] = useState(false);
  const { showToast } = useToast();

  // Form states
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [tests, setTests] = useState<any[]>([]);
  const [panels, setPanels] = useState<any[]>([]);
  const [paymentModes, setPaymentModes] = useState<string[]>(['CASH']);
  const [agencyOptions, setAgencyOptions] = useState<{value: string, label: string}[]>([]);
  const [patientOptions, setPatientOptions] = useState<{value: string, label: string}[]>([]);
  const [doctorOptions, setDoctorOptions] = useState<{value: string, label: string}[]>([]);
  const [items, setItems] = useState<OrderItem[]>([{
    test_id: 0,
    test_name: '',
    panel_id: 0,
    panel_name: '',
    is_panel: false,
    rate: 0,
    cgst_rate: 0,
    sgst_rate: 0,
    igst_rate: 0,
    cess_rate: 0,
    disc_percentage: 0,
    disc_amount: 0,
    taxable_amount: 0,
    cgst_amount: 0,
    sgst_amount: 0,
    igst_amount: 0,
    cess_amount: 0,
    total_amount: 0
  }]);

  const [totals, setTotals] = useState({
    subtotal: 0,
    itemsDiscountAmount: 0,
    taxableAmount: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    cessAmount: 0,
    itemsTotal: 0,
    overallDiscAmount: 0,
    finalTotal: 0
  });

  // Number generators
  const generateOrderNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const fff = String(now.getMilliseconds()).padStart(3, '0');
    return `OIPTO-${tenantId}${dd}${mm}${yyyy}${hh}${min}${ss}${fff}`;
  };

  const generateInvoiceNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const fff = String(now.getMilliseconds()).padStart(3, '0');
    return `OIPINV-${tenantId}${dd}${mm}${yyyy}${hh}${min}${ss}${fff}`;
  };

  const generatePaymentNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const fff = String(now.getMilliseconds()).padStart(3, '0');
    return `OIPPAY-${tenantId}${dd}${mm}${yyyy}${hh}${min}${ss}${fff}`;
  };

  const [formData, setFormData] = useState({
    test_order_number: generateOrderNumber(),
    order_date: new Date().toISOString().split('T')[0],
    patient_id: '',
    patient_name: '',
    patient_phone: '',
    doctor_id: '',
    doctor_name: '',
    doctor_phone: '',
    doctor_license_number: '',
    urgency: 'ROUTINE',
    status: 'ORDERED',
    agency_id: '',
    notes: '',
    disc_percentage: 0,
    roundoff: 0,
    invoice_number: generateInvoiceNumber(),
    invoice_notes: '',
    payment_number: generatePaymentNumber(),
    payment_amount: 0,
    payment_mode: 'CASH',
    instrument_number: '',
    instrument_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    branch_name: '',
    ifsc_code: '',
    transaction_reference: '',
    payment_remarks: ''
  });

  const [isPatientReadonly, setIsPatientReadonly] = useState(false);
  const [isDoctorReadonly, setIsDoctorReadonly] = useState(false);

  useEffect(() => {
    loadOrders();
    loadInitialData();
  }, []);

  // Recalculate totals whenever items, overall discount or roundoff change
  useEffect(() => {
    setTotals(calculateTotals());
  }, [items, formData.disc_percentage, formData.roundoff]);

  // Keep track of previous final total so we can auto-update payment_amount
  const prevFinalRef = useRef<number>(0);
  useEffect(() => {
    const prev = prevFinalRef.current || 0;
    const cur = parseFloat(toSafeFixed((totals.finalTotal as number) || 0, 2));
    // If payment was not set (0) or it matched previous final total, update it to new final total
    if ((Number(formData.payment_amount) || 0) === 0 || Number(formData.payment_amount) === prev) {
      setFormData(prev => ({ ...prev, payment_amount: cur }));
    }
    prevFinalRef.current = cur;
  }, [totals.finalTotal]);

  // When items are added or removed, always override payment_amount to match final total
  useEffect(() => {
    const cur = parseFloat(toSafeFixed(totals.finalTotal as number, 2));
    setFormData(prev => ({ ...prev, payment_amount: cur }));
  }, [items.length, totals.finalTotal]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await diagnosticService.getTestOrders();
      setOrders(response.data);
    } catch (error) {
      showToast('error', 'Failed to load test orders');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadPatients(''),
        loadDoctors(''),
        loadTests(),
        loadPanels(),
        loadAgencies('').then(options => setAgencyOptions(options)),
        fetchPaymentModes(),
        loadInvoices()
      ]);
    } catch (error) {
      showToast('error', 'Failed to load initial data');
    }
  };

  const fetchPaymentModes = async () => {
    try {
      const response = await adminService.getTenantSettings();
      if (response.data?.payment_modes) {
        setPaymentModes(response.data.payment_modes);
      }
    } catch (error) {
      console.log('Failed to load payment modes, using defaults');
    }
  };

  const loadPatients = async (search: string = '') => {
    try {
      const response = await patientService.getPatients({ search, per_page: 1000 });
      const patientList = response.data || [];
      setPatients(patientList);
      const options = patientList.map((patient: any) => {
        const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
        return {
          value: patient.id.toString(),
          label: `${patient.phone || ''}|${fullName}`.trim()
        };
      });
      setPatientOptions(options);
      return options;
    } catch (error) {
      showToast('error', 'Failed to load patients');
      return [];
    }
  };

  const loadDoctors = async (search: string = '') => {
    try {
      const response = await doctorService.getDoctors({ search, per_page: 1000 });
      const doctorList = response.data || [];
      setDoctors(doctorList);
      const options = doctorList.map((doctor: any) => {
        const fullName = `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim();
        return {
          value: doctor.id.toString(),
          label: `${doctor.phone || ''}|${fullName}`.trim()
        };
      });
      setDoctorOptions(options);
      return options;
    } catch (error) {
      showToast('error', 'Failed to load doctors');
      return [];
    }
  };

  const loadTests = async () => {
    try {
      const response = await careService.getTests({ per_page: 1000 });
      setTests(response.data);
    } catch (error) {
      showToast('error', 'Failed to load tests');
    }
  };

  const loadPanels = async () => {
    try {
      const response = await diagnosticService.getTestPanels({ per_page: 1000 });
      setPanels(response.data);
    } catch (error) {
      showToast('error', 'Failed to load panels');
    }
  };

  const loadAgencies = async (search: string) => {
    try {
      const response = await adminService.getAgencies({ search, per_page: 50 });
      return response.data.map((agency: any) => ({
        value: agency.id.toString(),
        label: `${agency.phone} | ${agency.name}`
      }));
    } catch (error) {
      return [];
    }
  };

  const loadInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const response = await healthService.getTestInvoices();
      const data = response.data.data || response.data || [];
      setInvoices(data);
    } catch (error) {
      showToast('error', 'Failed to load test invoices');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'disc_percentage') {
      setFormData(prev => ({ ...prev, disc_percentage: parseFloat(value) || 0 }));
    } else if (name === 'disc_amount') {
      const discAmount = parseFloat(value) || 0;
      const itemsTotal = totals.itemsTotal || 0;
      const discPercentage = itemsTotal > 0 ? (discAmount / itemsTotal) * 100 : 0;
      setFormData(prev => ({ ...prev, disc_percentage: discPercentage }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'roundoff' || name === 'payment_amount' ? parseFloat(value) || 0 : value
      }));
    }
  };

  const handlePaymentModeChange = (mode: string) => {
    setFormData(prev => ({
      ...prev,
      payment_mode: mode,
      instrument_number: '',
      instrument_date: new Date().toISOString().split('T')[0],
      bank_name: '',
      branch_name: '',
      ifsc_code: '',
      transaction_reference: ''
    }));
  };

  const handlePatientChange = (value: string | number | (string | number)[]) => {
    const patientId = value.toString();
    let patient = patients.find(p => p.id === Number(patientId));
    
    if (!patient) {
      if (formData.patient_id === patientId) {
        return;
      }
    }
    
    const fullName = patient ? `${patient.first_name || ''} ${patient.last_name || ''}`.trim() : '';
    setFormData(prev => ({
      ...prev,
      patient_id: patientId,
      patient_name: fullName,
      patient_phone: patient?.phone || ''
    }));
  };

  const handleDoctorChange = (value: string | number | (string | number)[]) => {
    const doctorId = value.toString();
    let doctor = doctors.find(d => d.id === Number(doctorId));
    
    if (!doctor) {
      if (formData.doctor_id === doctorId) {
        return;
      }
    }
    
    const fullName = doctor ? `${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() : '';
    setFormData(prev => ({
      ...prev,
      doctor_id: doctorId,
      doctor_name: fullName,
      doctor_phone: doctor?.phone || '',
      doctor_license_number: doctor?.license_number || ''
    }));
  };

  const handleTestChange = (index: number, value: string | number | (string | number)[]) => {
    const testId = Array.isArray(value) ? value[0] : value;
    const test = tests.find(t => t.id === Number(testId));
    
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      test_id: Number(testId),
      test_name: test?.name || '',
      panel_id: 0,
      panel_name: '',
      is_panel: false,
      rate: test?.rate || test?.cost || 0,
      disc_percentage: 0,
      disc_amount: 0,
      cgst_rate: (test?.gst || 0) / 2,
      sgst_rate: (test?.gst || 0) / 2,
      igst_rate: 0,
      cess_rate: test?.cess || 0
    };
    
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const handlePanelChange = (index: number, value: string | number | (string | number)[]) => {
    const panelId = Array.isArray(value) ? value[0] : value;
    const panel = panels.find(p => p.id === Number(panelId));
    
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      test_id: 0,
      test_name: '',
      panel_id: Number(panelId),
      panel_name: panel?.name || '',
      is_panel: true,
      rate: panel?.rate || panel?.cost || 0,
      disc_percentage: 0,
      disc_amount: 0,
      cgst_rate: (panel?.gst || 0) / 2,
      sgst_rate: (panel?.gst || 0) / 2,
      igst_rate: 0,
      cess_rate: panel?.cess || 0
    };
    
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const handleItemChange = (index: number, field: keyof OrderItem, value: number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const calculateItemTotal = (index: number, itemsArray: OrderItem[]) => {
    const item = itemsArray[index];
    const baseAmount = item.rate;
    const discountAmount = baseAmount * (item.disc_percentage / 100);
    const taxableAmount = baseAmount - discountAmount;
    const cgstAmount = taxableAmount * (item.cgst_rate / 100);
    const sgstAmount = taxableAmount * (item.sgst_rate / 100);
    const igstAmount = taxableAmount * (item.igst_rate / 100);
    const cessAmount = taxableAmount * (item.cess_rate / 100);
    const total = taxableAmount + cgstAmount + sgstAmount + igstAmount + cessAmount;

    itemsArray[index] = {
      ...item,
      disc_amount: discountAmount,
      taxable_amount: taxableAmount,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      igst_amount: igstAmount,
      cess_amount: cessAmount,
      total_amount: total
    };
    setItems([...itemsArray]);
  };

  const addItem = () => {
    setItems([...items, {
      test_id: 0,
      test_name: '',
      panel_id: 0,
      panel_name: '',
      is_panel: false,
      rate: 0,
      cgst_rate: 0,
      sgst_rate: 0,
      igst_rate: 0,
      cess_rate: 0,
      disc_percentage: 0,
      disc_amount: 0,
      taxable_amount: 0,
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,
      cess_amount: 0,
      total_amount: 0
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.total_amount, 0);
    const itemsDiscountAmount = items.reduce((sum, item) => sum + item.disc_amount, 0);
    const taxableAmount = items.reduce((sum, item) => sum + item.taxable_amount, 0);
    const cgstAmount = items.reduce((sum, item) => sum + item.cgst_amount, 0);
    const sgstAmount = items.reduce((sum, item) => sum + item.sgst_amount, 0);
    const igstAmount = items.reduce((sum, item) => sum + item.igst_amount, 0);
    const cessAmount = items.reduce((sum, item) => sum + item.cess_amount, 0);
    const itemsTotal = items.reduce((sum, item) => sum + item.total_amount, 0);
    const overallDiscAmount = parseFloat(toSafeFixed(itemsTotal * (formData.disc_percentage / 100), 2));
    const finalTotal = parseFloat(toSafeFixed(itemsTotal - overallDiscAmount + formData.roundoff, 2));
    return { subtotal, itemsDiscountAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount, itemsTotal, overallDiscAmount, finalTotal };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.patient_name) {
      showToast('error', 'Please select a patient');
      return;
    }
    if (!formData.doctor_id) {
      showToast('error', 'Please select a doctor');
      return;
    }

    const validItems = items.filter(item => (item.test_id > 0 || item.panel_id > 0)).map((item, idx) => ({
      line_no: idx + 1,
      test_id: item.test_id > 0 ? item.test_id : null,
      test_name: item.test_name,
      panel_id: item.panel_id > 0 ? item.panel_id : null,
      panel_name: item.panel_name,
      rate: item.rate,
      disc_percentage: item.disc_percentage,
      disc_amount: item.disc_amount,
      taxable_amount: item.taxable_amount,
      cgst_rate: item.cgst_rate,
      cgst_amount: item.cgst_amount,
      sgst_rate: item.sgst_rate,
      sgst_amount: item.sgst_amount,
      igst_rate: item.igst_rate,
      igst_amount: item.igst_amount,
      cess_rate: item.cess_rate,
      cess_amount: item.cess_amount,
      total_amount: item.total_amount,
      //item_status: 'PENDING',
      remarks: ''
    }));

    if (validItems.length === 0) {
      showToast('error', 'Please add at least one test or panel');
      return;
    }

    try {
      setLoading(true);
      const { subtotal, itemsDiscountAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount, overallDiscAmount, finalTotal } = totals;
      
      const orderData = {
        test_order_number: formData.test_order_number,
        order_date: new Date(formData.order_date).toISOString(),
        patient_id: formData.patient_id ? Number(formData.patient_id) : null,
        patient_name: formData.patient_name,
        patient_phone: formData.patient_phone,
        doctor_id: formData.doctor_id ? Number(formData.doctor_id) : null,
        doctor_name: formData.doctor_name || null,
        doctor_phone: formData.doctor_phone || null,
        doctor_license_number: formData.doctor_license_number || null,
        //appointment_id: 0,
        agency_id: formData.agency_id ? Number(formData.agency_id) : null,
        subtotal_amount: subtotal,
        items_total_discount_amount: itemsDiscountAmount,
        taxable_amount: taxableAmount,
        cgst_amount: cgstAmount,
        sgst_amount: sgstAmount,
        igst_amount: igstAmount,
        cess_amount: cessAmount,
        overall_disc_percentage: formData.disc_percentage,
        overall_disc_amount: overallDiscAmount,
        overall_cess_percentage: 0,
        overall_cess_amount: 0,
        roundoff: formData.roundoff,
        final_amount: finalTotal,
        urgency: formData.urgency,
        //status: formData.status,
        status: 'ORDERED',
        notes: formData.notes,
        tags: null,
        items: validItems,
        invoice_number: formData.invoice_number,
        invoice_notes: formData.invoice_notes,
        payment_number: formData.payment_number,
        payment_amount: formData.payment_amount || finalTotal,
        payment_mode: formData.payment_mode,
        instrument_number: formData.instrument_number,
        instrument_date: formData.instrument_date,
        bank_name: formData.bank_name,
        branch_name: formData.branch_name,
        ifsc_code: formData.ifsc_code,
        transaction_reference: formData.transaction_reference,
        payment_remarks: formData.payment_remarks
      };

      const response = await diagnosticService.createTestOrderWithBillingAndPayment(orderData);
      
      if (response.success) {
        showToast('success', 'Test order created successfully');
        await loadOrders();
        await loadInvoices();
        resetForm();
      } else {
        showToast('error', response.message || 'Failed to create test order');
      }
    } catch (error) {
      showToast('error', 'Failed to create test order');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      test_order_number: generateOrderNumber(),
      order_date: new Date().toISOString().split('T')[0],
      patient_id: '',
      patient_name: '',
      patient_phone: '',
      doctor_id: '',
      doctor_name: '',
      doctor_phone: '',
      doctor_license_number: '',
      urgency: 'ROUTINE',
      status: 'ORDERED',
      agency_id: '',
      notes: '',
      disc_percentage: 0,
      roundoff: 0,
      invoice_number: generateInvoiceNumber(),
      invoice_notes: '',
      payment_number: generatePaymentNumber(),
      payment_amount: 0,
      payment_mode: 'CASH',
      instrument_number: '',
      instrument_date: new Date().toISOString().split('T')[0],
      bank_name: '',
      branch_name: '',
      ifsc_code: '',
      transaction_reference: '',
      payment_remarks: ''
    });
    setIsPatientReadonly(false);
    setIsDoctorReadonly(false);
    setItems([{
      test_id: 0,
      test_name: '',
      panel_id: 0,
      panel_name: '',
      is_panel: false,
      rate: 0,
      cgst_rate: 0,
      sgst_rate: 0,
      igst_rate: 0,
      cess_rate: 0,
      disc_percentage: 0,
      disc_amount: 0,
      taxable_amount: 0,
      cgst_amount: 0,
      sgst_amount: 0,
      igst_amount: 0,
      cess_amount: 0,
      total_amount: 0
    }]);
  };

  const handleView = (order: OrderPurchasePayment) => {
    setSelectedOrder(order);
    setShowOrderView(true);
  };

  const { subtotal, itemsDiscountAmount, taxableAmount, cgstAmount, sgstAmount, igstAmount, cessAmount, itemsTotal, overallDiscAmount, finalTotal } = totals;
  const pendingAmount = Math.max(0, parseFloat(toSafeFixed((finalTotal || 0) - parseFloat(toSafeFixed((Number(formData.payment_amount) || 0), 2)), 2)));

  const handleInvoicePrint = async (invoice: any) => {
    try {
      const response = await healthService.getTestInvoice(invoice.id, true);
      const invoiceData = response.data.data || response.data;
      setSelectedInvoice(invoiceData);
      setShowInvoiceView(true);
    } catch (error) {
      showToast('error', 'Failed to load invoice for printing');
    }
  };

  const invoiceColumns = [
    { key: 'invoice_number', label: 'Invoice #', sortable: true },
    { 
      key: 'order', 
      label: 'Order #', 
      sortable: true,
      render: (_: any, invoice: any) => invoice.order?.test_order_number || 'N/A'
    },
    { key: 'patient_name', label: 'Patient', sortable: true },
    { 
      key: 'invoice_date', 
      label: 'Date', 
      sortable: true,
      render: (value: string) => formatUTCToLocal(value),  
    },
    {
      key: 'final_amount',
      label: 'Amount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'paid_amount',
      label: 'Paid Amount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'balance_amount',
      label: 'Pedning Amount',
      sortable: true,
      render: (value: number) => toSafeFixed(value || 0, 2),
    },
    {
      key: 'payment_status',
      label: 'Payment Status',
      sortable: true,
      render: (value: string) => (
        <span style={{
          padding: '2px 8px',
          fontSize: 'var(--erp-font-size-xs)',
          borderRadius: '4px',
          backgroundColor: value === 'PAID' ? '#dcfce7' : value === 'PARTIAL' ? '#fef3c7' : '#fee2e2',
          color: value === 'PAID' ? '#166534' : value === 'PARTIAL' ? '#854d0e' : '#991b1b'
        }}>
          {value || 'UNPAID'}
        </span>
      ),
    },
    // {
    //   key: 'status',
    //   label: 'Status',
    //   sortable: true,
    //   render: (value: string) => (
    //     <span style={{
    //       padding: '2px 8px',
    //       fontSize: 'var(--erp-font-size-xs)',
    //       borderRadius: '4px',
    //       backgroundColor: value === 'POSTED' ? '#dcfce7' : value === 'DRAFT' ? '#fef3c7' : '#fee2e2',
    //       color: value === 'POSTED' ? '#166534' : value === 'DRAFT' ? '#854d0e' : '#991b1b'
    //     }}>
    //       {value || 'DRAFT'}
    //     </span>
    //   ),
    // },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, invoice: any) => (
        <div className="flex space-x-0">
          <button 
            onClick={() => handleInvoicePrint(invoice)} 
            className="text-green-600 hover:text-green-800 cursor-pointer"
            title="Print Invoice"
          >
            <Printer className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  const columns = [
    {
      key: 'test_order_number',
      label: 'Order Number',
      sortable: true,
    },
    {
      key: 'patient_name',
      label: 'Patient',
      sortable: true,
    },
    {
      key: 'doctor_name',
      label: 'Doctor',
      sortable: true,
    },
    {
      key: 'order_date',
      label: 'Date',
      sortable: true,
      render: (value: string) => formatUTCToLocal(value),
    },
    {
      key: 'urgency',
      label: 'Urgency',
      sortable: true,
      render: (value: string) => (
        <span style={{
          padding: '1px 6px',
          fontSize: 'var(--erp-font-size-xs)',
          borderRadius: '4px',
          backgroundColor: value === 'urgent' ? '#fee2e2' : value === 'normal' ? '#dbeafe' : '#f3f4f6',
          color: value === 'urgent' ? '#991b1b' : value === 'normal' ? '#1e40af' : '#6b7280'
        }}>
          {value || 'Normal'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value: string) => (
        <span style={{
          padding: '1px 6px',
          fontSize: 'var(--erp-font-size-xs)',
          borderRadius: '4px',
          backgroundColor: value === 'completed' ? '#dcfce7' : value === 'pending' ? '#fef3c7' : '#f3f4f6',
          color: value === 'completed' ? '#166534' : value === 'pending' ? '#854d0e' : '#6b7280'
        }}>
          {value || 'Pending'}
        </span>
      ),
    },
     {
      key: 'subtotal_amount',
      label: 'Sub Total',
      sortable: true,
      render: (value: number) => (value || 0),
    },
     {
      key: 'items_total_discount_amount',
      label: 'Item Disc',
      sortable: true,
      render: (value: number) => (value || 0),
    },
     {
      key: 'taxable_amount',
      label: 'Taxable',
      sortable: true,
      render: (value: number) => (value || 0),
    },
    {
      key: 'total_tax',
      label: 'Total Tax',
      sortable: true,
      render: (_: any, order: OrderPurchasePayment) => {
        const total = (order.cgst_amount || 0) + (order.sgst_amount || 0) + (order.igst_amount || 0) + (order.cess_amount || 0);
        return total;
      },
    },
    {
      key: 'final_amount',
      label: 'Total Amount',
      sortable: true,
      render: (value: number) => (value || 0),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_: any, order: OrderPurchasePayment) => (
        <div className="flex space-x-0">
          <button
            onClick={() => handleView(order)}
            className="text-blue-600 hover:text-blue-800"
            title="Print"
          >
            <Printer className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleView(order)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleView(order)}
            className="text-red-600 hover:text-red-800"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  if (showInvoiceView && selectedInvoice) {
    return (
      <TestInvoiceView
        invoice={selectedInvoice}
        onBack={() => {
          setShowInvoiceView(false);
          setSelectedInvoice(null);
        }}
      />
    );
  }

  if (showOrderView && selectedOrder) {
    return (
      <TestOrderView
        order={selectedOrder}
        onBack={() => {
          setShowOrderView(false);
          setSelectedOrder(null);
        }}
      />
    );
  }

  return ( 
    <div style={{ padding: 'var(--erp-spacing-lg)' }}>
      <div className="bg-white rounded-lg shadow mb-4 mt-1">
        <div className="border-b border-gray-200 flex justify-between items-center" style={{ padding: 'var(--erp-section-padding)' }}>
          <h2 className="font-medium text-gray-800" style={{ fontSize: 'var(--erp-datatable-title-font-size)', lineHeight: 'var(--erp-line-height)' }}>Create Test Order with Billing & Payment</h2>
          <button
            type="button"
            onClick={() => setIsFormCollapsed(!isFormCollapsed)}
            className="text-gray-500 hover:text-gray-700"
          >
            {isFormCollapsed ? <ChevronDown className="erp-form-btn-icon" style={{ marginRight: 0 }} /> : <ChevronUp className="erp-form-btn-icon" style={{ marginRight: 0 }} />}
          </button>
        </div>

        {!isFormCollapsed && (
          <form onSubmit={handleSubmit} style={{ padding: 'var(--erp-card-padding)' }}>
            {/* Main Form Details Row */}
            <div className="grid grid-cols-1 sm:grid-cols-6" style={{ gap: 'var(--erp-spacing-lg)', marginBottom: 'var(--erp-spacing-xl)' }}>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Order Number</label>
                <input
                  type="text"
                  name="test_order_number"
                  value={formData.test_order_number}
                  readOnly
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
                <DatePicker
                  value={formData.order_date}
                  onChange={(value) => setFormData(prev => ({ ...prev, order_date: value }))}
                  placeholder="Select order date"
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Patient * <span className="text-xs text-gray-500">(Format: Phone|Name)</span></label>
                <SearchableDropdown
                  options={patientOptions}
                  value={formData.patient_id}
                  onChange={handlePatientChange}
                  onSearch={async (search) => {
                    const options = await loadPatients(search);
                    setPatientOptions(options);
                    return options;
                  }}
                  onAdd={async (inputValue) => {
                    const parts = inputValue.split('|').map(p => p.trim());
                    const phone = parts[0] || '';
                    const fullName = parts[1] || parts[0];
                    const nameParts = fullName.split(' ');
                    const first_name = nameParts[0] || '';
                    const last_name = nameParts.slice(1).join(' ') || '';
                    try {
                      const response = await patientService.createPatient({ first_name, last_name, phone });
                      const newPatient = response.data || response;
                      const displayName = `${newPatient.first_name || first_name} ${newPatient.last_name || last_name}`.trim();
                      const newOption = { 
                        value: newPatient.id.toString(), 
                        label: `${newPatient.phone || phone}|${displayName}` 
                      };
                      setPatients(prev => [...prev, newPatient]);
                      setPatientOptions(prev => [...prev, newOption]);
                      setFormData(prev => ({
                        ...prev,
                        patient_id: newPatient.id.toString(),
                        patient_name: displayName,
                        patient_phone: newPatient.phone || phone
                      }));
                      return newOption;
                    } catch (error) {
                      showToast('error', 'Failed to create patient');
                      throw error;
                    }
                  }}
                  placeholder="Search or add patient"
                  multiple={false}
                  searchable={true}
                  allowAdd={true}
                  disabled={isPatientReadonly}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Doctor* <span className="text-xs text-gray-500">(Format: Phone|Name)</span></label>
                <SearchableDropdown
                  options={doctorOptions}
                  value={formData.doctor_id}
                  onChange={handleDoctorChange}
                  onSearch={async (search) => {
                    const options = await loadDoctors(search);
                    setDoctorOptions(options);
                    return options;
                  }}
                  onAdd={async (inputValue) => {
                    const parts = inputValue.split('|').map(p => p.trim());
                    const phone = parts[0] || '';
                    const fullName = parts[1] || parts[0];
                    const nameParts = fullName.split(' ');
                    const first_name = nameParts[0] || '';
                    const last_name = nameParts.slice(1).join(' ') || '';
                    try {
                      const response = await doctorService.createDoctor({ first_name, last_name, phone });
                      const newDoctor = response.data || response;
                      const displayName = `${newDoctor.first_name || first_name} ${newDoctor.last_name || last_name}`.trim();
                      const newOption = { 
                        value: newDoctor.id.toString(), 
                        label: `${newDoctor.phone || phone}|${displayName}` 
                      };
                      setDoctors(prev => [...prev, newDoctor]);
                      setDoctorOptions(prev => [...prev, newOption]);
                      setFormData(prev => ({
                        ...prev,
                        doctor_id: newDoctor.id.toString(),
                        doctor_name: displayName,
                        doctor_phone: newDoctor.phone || phone,
                        doctor_license_number: newDoctor.license_number || ''
                      }));
                      return newOption;
                    } catch (error) {
                      showToast('error', 'Failed to create doctor');
                      throw error;
                    }
                  }}
                  placeholder="Search or add doctor"
                  multiple={false}
                  searchable={true}
                  allowAdd={true}
                  disabled={isDoctorReadonly}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Urgency</label>
                <SearchableDropdown
                  options={[
                    { value: 'ROUTINE', label: 'ROUTINE' },
                    { value: 'URGENT', label: 'URGENT' },
                    { value: 'STAT', label: 'STAT' },
                    { value: 'CRITICAL', label: 'CRITICAL' }
                  ]}
                  value={formData.urgency}
                  onChange={(value) => setFormData(prev => ({ ...prev, urgency: value.toString() }))}
                  placeholder="Select urgency"
                  multiple={false}
                  searchable={true}
                />
              </div>

              {/* <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <SearchableDropdown
                  options={[
                    { value: 'DRAFT', label: 'DRAFT' },
                    { value: 'ORDERED', label: 'ORDERED' },
                    { value: 'CANCELLED', label: 'CANCELLED' }
                  ]}
                  value={formData.status}
                  onChange={(value) => setFormData(prev => ({ ...prev, status: value.toString() }))}
                  placeholder="Select status"
                  multiple={false}
                  searchable={true}
                />
              </div> */}

                <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Agency <span className="text-xs text-gray-500">(Format: Phone | Name)</span></label>
                <SearchableDropdown
                  options={agencyOptions}
                  value={formData.agency_id}
                  onChange={(value) => setFormData(prev => ({ ...prev, agency_id: value.toString() }))}
                  onSearch={async (search) => {
                    const options = await loadAgencies(search);
                    setAgencyOptions(options);
                    return options;
                  }}
                  onAdd={async (inputValue) => {
                    const parts = inputValue.split('|').map(p => p.trim());
                    const phone = parts[0] || '';
                    const name = parts[1] || parts[0];
                    const response = await adminService.createAgency({ name, phone });
                    const newOption = { value: response.data.id.toString(), label: `${phone} | ${name}` };
                    setAgencyOptions(prev => [...prev, newOption]);
                    return newOption;
                  }}
                  placeholder="Search agency"
                  multiple={false}
                  searchable={true}
                  allowAdd={true}
                />
              </div>
              
            </div>

            {/* Agency & Notes Row */}
            <div className="grid grid-cols-1 sm:grid-cols-6" style={{ gap: 'var(--erp-spacing-lg)', marginBottom: 'var(--erp-spacing-xl)' }}>
            

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <input
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                />
              </div>
            </div>

            {/* Order Items Section */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-md font-semibold">Order Items</h3>
              <button
                type="button"
                onClick={addItem}
                className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Item
              </button>
            </div>

            <div className="mb-6">
              <style>{`
                .test-order-table { table-layout: fixed !important; }
                .test-order-table th:nth-child(1), .test-order-table td:nth-child(1) { width: 5% !important; }
                .test-order-table th:nth-child(2), .test-order-table td:nth-child(2) { width: 15% !important; }
                .test-order-table th:nth-child(3), .test-order-table td:nth-child(3) { width: 7% !important; }
                .test-order-table th:nth-child(4), .test-order-table td:nth-child(4) { width: 6% !important; }
                .test-order-table th:nth-child(5), .test-order-table td:nth-child(5) { width: 6% !important; }
                .test-order-table th:nth-child(6), .test-order-table td:nth-child(6) { width: 6% !important; }
                .test-order-table th:nth-child(7), .test-order-table td:nth-child(7) { width: 7% !important; }
                .test-order-table th:nth-child(8), .test-order-table td:nth-child(8) { width: 8% !important; }
                .test-order-table th:nth-child(9), .test-order-table td:nth-child(9) { width: 8% !important; }
                .test-order-table th:nth-child(10), .test-order-table td:nth-child(10) { width: 8% !important; }
                .test-order-table th:nth-child(11), .test-order-table td:nth-child(11) { width: 8% !important; }
                .test-order-table th:nth-child(12), .test-order-table td:nth-child(12) { width: 8% !important; }
                .test-order-table th:nth-child(13), .test-order-table td:nth-child(13) { width: 4% !important; }
              `}</style>
              <div className="overflow-x-auto">
                <table className="border border-gray-200 test-order-table" style={{ minWidth: '1200px', width: '100%' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Panel</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-left">Test/Panel</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Rate</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Disc%</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Disc Amt</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Taxable</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">GST%</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">GST Amt</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">CESS%</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">CESS Amt</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Total</th>
                      <th className="px-2 py-2 text-xs font-medium text-gray-500 uppercase text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-2 py-2 text-center" style={{ minWidth: '80px' }}>
                          <input
                            type="checkbox"
                            checked={item.is_panel}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index] = { ...newItems[index], is_panel: e.target.checked, test_id: 0, test_name: '', panel_id: 0, panel_name: '' };
                              setItems(newItems);
                            }}
                            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-2 py-2" style={{ minWidth: '180px' }}>
                          {item.is_panel ? (
                            <SearchableDropdown
                              options={panels.map(panel => ({
                                value: panel.id.toString(),
                                label: panel.name
                              }))}
                              value={item.panel_id.toString()}
                              onChange={(value) => handlePanelChange(index, value)}
                              placeholder="Select panel..."
                              multiple={false}
                              searchable={true}
                              className="w-full"
                            />
                          ) : (
                            <SearchableDropdown
                              options={tests.map(test => ({
                                value: test.id.toString(),
                                label: test.name
                              }))}
                              value={item.test_id.toString()}
                              onChange={(value) => handleTestChange(index, value)}
                              placeholder="Select test..."
                              multiple={false}
                              searchable={true}
                              className="w-full"
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 w-20">
                          <input
                            type="number"
                            value={item.rate}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                rate: parseFloat(e.target.value) || 0
                              };
                              setItems(newItems);
                              calculateItemTotal(index, newItems);
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step="0.01"
                          />
                        </td>
                        <td className="px-3 py-2 w-16">
                          <input
                            type="number"
                            value={item.disc_percentage}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                disc_percentage: parseFloat(e.target.value) || 0
                              };
                              setItems(newItems);
                              calculateItemTotal(index, newItems);
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step="0.1"
                          />
                        </td>
                        <td className="px-3 py-2 w-16">
                          <input
                            type="number"
                            value={item.disc_amount}
                            onChange={(e) => {
                              const discAmount = parseFloat(e.target.value) || 0;
                              const discPercentage = item.rate > 0 ? (discAmount / item.rate) * 100 : 0;
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                disc_percentage: discPercentage
                              };
                              setItems(newItems);
                              calculateItemTotal(index, newItems);
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step="0.01"
                          />
                        </td>
                        <td className="px-3 py-2 w-20 text-sm text-center">{item.taxable_amount}</td>
                        <td className="px-3 py-2 w-16">
                          <input
                            type="number"
                            value={item.cgst_rate + item.sgst_rate}
                            onChange={(e) => {
                              const totalGst = parseFloat(e.target.value) || 0;
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                cgst_rate: totalGst / 2,
                                sgst_rate: totalGst / 2
                              };
                              setItems(newItems);
                              calculateItemTotal(index, newItems);
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step="0.1"
                          />
                        </td>
                        <td className="px-3 py-2 w-20 text-sm text-center">{(item.cgst_amount + item.sgst_amount + item.igst_amount)}</td>
                        <td className="px-3 py-2 w-16">
                          <input
                            type="number"
                            value={item.cess_rate}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index] = {
                                ...newItems[index],
                                cess_rate: parseFloat(e.target.value) || 0
                              };
                              setItems(newItems);
                              calculateItemTotal(index, newItems);
                            }}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                            step="0.1"
                          />
                        </td>
                        <td className="px-3 py-2 w-20 text-sm text-center">{item.cess_amount}</td>
                        <td className="px-3 py-2 w-24 text-sm text-center">{item.total_amount ? toSafeFixed(item.total_amount, 2) : ''}</td>
                        <td className="px-3 py-2 w-16 text-center">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800"
                            disabled={items.length === 1}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Invoice & Payment Details Row */}
            <div className="flex flex-col md:flex-row gap-6 mb-6">
              <div className="w-full">
                <h4 className="font-semibold mb-4">Invoice & Payment Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number</label>
                    <input
                      type="text"
                      value={formData.invoice_number}
                      readOnly
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Payment Number</label>
                    <input
                      type="text"
                      value={formData.payment_number}
                      readOnly
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Notes</label>
                    <input
                      type="text"
                      name="invoice_notes"
                      value={formData.invoice_notes}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount *</label>
                    <NumericInput
                      name="payment_amount"
                      value={formData.payment_amount || 0}
                      onChange={(value) => setFormData(prev => ({ ...prev, payment_amount: value }))}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      step={0.01}
                      min={0}
                      allowNegative={false}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Pending Amount</label>
                    <input
                      type="text"
                      name="pending_amount"
                      value={toSafeFixed(pendingAmount, 2)}
                      readOnly
                      className="w-full px-2 py-1.5 text-sm border border-yellow-300 rounded bg-yellow-50 text-yellow-900 font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode *</label>
                    <SearchableDropdown
                      options={paymentModes.map(mode => ({ value: mode, label: mode }))}
                      value={formData.payment_mode}
                      onChange={(v) => handlePaymentModeChange(v.toString())}
                      placeholder="Select mode"
                      multiple={false}
                      searchable={false}
                    />
                  </div>


                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Payment Remarks</label>
                    <input
                      type="text"
                      name="payment_remarks"
                      value={formData.payment_remarks}
                      onChange={handleInputChange}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                    />
                  </div>


                </div>
              </div>
            </div>

            {/* Payment Mode & Related Fields Row */}
            <div className="flex flex-col md:flex-row gap-6 mb-6">
              <div className="w-full">
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">

                  {['CHEQUE', 'CARD', 'BANK_TRANSFER'].includes(formData.payment_mode) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Instrument Number *</label>
                      <input
                        type="text"
                        name="instrument_number"
                        value={formData.instrument_number}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                        required
                      />
                    </div>
                  )}

                  {['CHEQUE', 'CARD', 'UPI', 'BANK_TRANSFER'].includes(formData.payment_mode) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Instrument Date *</label>
                      <DatePicker
                        value={formData.instrument_date}
                        onChange={(v) => setFormData(prev => ({ ...prev, instrument_date: v }))}
                        required
                      />
                    </div>
                  )}

                  {['CHEQUE', 'BANK_TRANSFER'].includes(formData.payment_mode) && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name *</label>
                        <input
                          type="text"
                          name="bank_name"
                          value={formData.bank_name}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Branch Name</label>
                        <input
                          type="text"
                          name="branch_name"
                          value={formData.branch_name}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">IFSC Code</label>
                        <input
                          type="text"
                          name="ifsc_code"
                          value={formData.ifsc_code}
                          onChange={handleInputChange}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                        />
                      </div>
                    </>
                  )}

                  {formData.payment_mode === 'UPI' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Transaction Reference</label>
                      <input
                        type="text"
                        name="transaction_reference"
                        value={formData.transaction_reference}
                        onChange={handleInputChange}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Summary Section */}
            <div className="flex flex-col md:flex-row justify-end gap-6 mb-6">
              <div className="w-full md:w-1/2 erp-summary-container">
                <h4 className="font-semibold" style={{ marginBottom: 'var(--erp-spacing-lg)' }}>Order Summary</h4>
                <div>
                  <div className="erp-summary-row">
                    <span className="erp-summary-label">Subtotal:</span>
                    <span className="erp-summary-value">{toSafeFixed(subtotal, 2)}</span>
                  </div>
                  <div className="erp-summary-row">
                    <span className="erp-summary-label">Discount %:</span>
                    <NumericInput
                      name="disc_percentage"
                      value={formData.disc_percentage}
                      onChange={(value) => setFormData(prev => ({ ...prev, disc_percentage: value }))}
                      className="erp-summary-input"
                      step={0.1}
                      decimalPlaces={2}
                      allowNegative={false}
                      max={100}
                    />
                  </div>
                  <div className="erp-summary-row">
                    <span className="erp-summary-label">Discount Amount:</span>
                    <NumericInput
                      name="disc_amount"
                      value={overallDiscAmount}
                      onChange={(value) => setFormData(prev => ({ ...prev, disc_percentage: itemsTotal > 0 ? (value / itemsTotal) * 100 : 0 }))}
                      className="erp-summary-input"
                      step={0.01}
                      decimalPlaces={2}
                      allowNegative={false}
                      max={subtotal}
                    />
                  </div>
                  <div className="erp-summary-row erp-summary-total">
                    <span className="erp-summary-label">Total:</span>
                    <span className="erp-summary-value">{toSafeFixed(finalTotal, 2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row justify-center" style={{ gap: 'var(--erp-spacing-lg)', marginTop: 'var(--erp-spacing-xl)' }}>
              <button
                type="button"
                onClick={resetForm}
                disabled={loading}
                className="erp-form-btn text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset
              </button>
              <button
                type="submit"
                disabled={loading}
                className="erp-form-btn text-white bg-primary hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      <div className="mt-6">
        <DataTable
          title="Test Invoices"
          data={invoices}
          columns={invoiceColumns}
          loading={invoicesLoading}
        />
      </div>
    </div>
  );
};

export default OrderPurchasePaymentPage;